import { type JSX, useCallback, useEffect, useState } from "react"
import { Modal } from "../shared/modal"
import {
  type SharingResult,
  type SharingState,
  changeGrantRole,
  disableLink,
  enableLink,
  fetchSharing,
  revokeGrant,
  rotateLink,
  shareWithEmail,
  shareWithOrganization,
} from "./sharing_api"

const ROLES: { value: "viewer" | "editor"; label: string }[] = [
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
]

// "Share this workspace" — the people list, the organization toggle, and the
// link. Loads its state when it opens rather than on mount, so a menu that is
// never used costs nothing.
export function ShareDialog({
  open,
  workspaceName,
  sharingUrl,
  onClose,
}: {
  open: boolean
  workspaceName: string
  sharingUrl: string
  onClose: () => void
}): JSX.Element {
  const [state, setState] = useState<SharingState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<"viewer" | "editor">("viewer")
  const [copied, setCopied] = useState(false)

  // Every mutation answers with the whole state, so one handler applies them
  // all and the dialog never holds a view the server disagrees with.
  const apply = useCallback((result: SharingResult): boolean => {
    if ("error" in result) {
      setError(result.error)
      return false
    }
    setState(result.state)
    setError(null)
    return true
  }, [])

  const run = useCallback(
    async (action: () => Promise<SharingResult>): Promise<boolean> => {
      setBusy(true)
      const ok = apply(await action())
      setBusy(false)
      return ok
    },
    [apply],
  )

  useEffect(() => {
    if (!open) return
    setError(null)
    setCopied(false)
    void run(() => fetchSharing(sharingUrl))
  }, [open, sharingUrl, run])

  async function addPerson(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    const address = email.trim()
    if (!address) return

    if (await run(() => shareWithEmail(sharingUrl, address, role))) {
      setEmail("")
    }
  }

  async function copyLink(): Promise<void> {
    if (!state?.link.url) return
    try {
      await navigator.clipboard.writeText(state.link.url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setError("Could not copy — select the link and copy it by hand.")
    }
  }

  return (
    <Modal open={open} title={`Share “${workspaceName}”`} onClose={onClose} wide>
      {error && <div className="modal__errors">{error}</div>}

      {!state ? (
        <p className="modal__text">Loading…</p>
      ) : (
        <div className="share">
          <form className="share__invite" onSubmit={addPerson}>
            <input
              type="email"
              className="share__email"
              placeholder="Add people by email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
              aria-label="Email address to share with"
            />
            <select
              className="share__role"
              value={role}
              onChange={(event) => setRole(event.target.value as "viewer" | "editor")}
              disabled={busy}
              aria-label="Role for the person you are adding"
            >
              {ROLES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !email.trim()}>
              Share
            </button>
          </form>

          <section className="share__section">
            <h3 className="share__heading">People with access</h3>

            <ul className="share__list">
              <li className="share__row">
                <div className="share__who">
                  <span className="share__name">{state.owner.name}</span>
                  <span className="share__email-label">{state.owner.email}</span>
                </div>
                <span className="share__fixed-role">Owner</span>
              </li>

              {state.people.map((person) => (
                <li className="share__row" key={person.id}>
                  <div className="share__who">
                    <span className="share__name">
                      {person.name ?? person.email}
                      {person.pending && <span className="share__pending" title="They get access as soon as they verify an account">Pending</span>}
                    </span>
                    {person.name && <span className="share__email-label">{person.email}</span>}
                  </div>

                  <select
                    className="share__role"
                    value={person.role}
                    disabled={busy}
                    aria-label={`Role for ${person.email}`}
                    onChange={(event) => {
                      const next = event.target.value
                      if (next === "remove") {
                        void run(() => revokeGrant(sharingUrl, person.id))
                      } else {
                        void run(() => changeGrantRole(sharingUrl, person.id, next))
                      }
                    }}
                  >
                    {ROLES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                    <option value="remove">Remove access</option>
                  </select>
                </li>
              ))}
            </ul>
          </section>

          <section className="share__section">
            <h3 className="share__heading">General access</h3>

            <div className="share__row">
              <div className="share__who">
                <span className="share__name">{state.organization.name}</span>
                <span className="share__email-label">
                  {state.organization.shared
                    ? `Everyone in your organization (${state.organization.member_count})`
                    : "Only people you add above"}
                </span>
              </div>

              <select
                className="share__role"
                value={state.organization.shared ? (state.organization.role ?? "viewer") : "off"}
                disabled={busy}
                aria-label="Organization access"
                onChange={(event) => {
                  const next = event.target.value
                  if (next === "off") {
                    // Same revoke call as removing a person — the organization
                    // row is a grant like any other, it just renders as a toggle.
                    const id = state.organization.id
                    if (id !== null) void run(() => revokeGrant(sharingUrl, id))
                  } else {
                    void run(() => shareWithOrganization(sharingUrl, next))
                  }
                }}
              >
                <option value="off">Not shared</option>
                {ROLES.map((option) => (
                  <option key={option.value} value={option.value}>{`Organization can ${option.label.toLowerCase()}`}</option>
                ))}
              </select>
            </div>

            <div className="share__row">
              <div className="share__who">
                <span className="share__name">Anyone with the link</span>
                <span className="share__email-label">
                  {state.link.enabled
                    ? "Anyone who has the link can view — they cannot edit or start agents"
                    : "The link is off"}
                </span>
              </div>

              <select
                className="share__role"
                value={state.link.enabled ? "on" : "off"}
                disabled={busy}
                aria-label="Link sharing"
                onChange={(event) => {
                  void run(() => (event.target.value === "on" ? enableLink(sharingUrl) : disableLink(sharingUrl)))
                }}
              >
                <option value="off">Off</option>
                <option value="on">Anyone with the link can view</option>
              </select>
            </div>

            {state.link.enabled && state.link.url && (
              <div className="share__link">
                <input className="share__link-url" readOnly value={state.link.url} aria-label="Share link" onFocus={(e) => e.target.select()} />
                <button type="button" className="btn btn--sm" onClick={() => void copyLink()}>
                  {copied ? "Copied" : "Copy link"}
                </button>
                {/* The one thing turning the link off and on again cannot do:
                    burn a link you sent to the wrong place while the people who
                    have it legitimately keep working. */}
                <button type="button" className="btn btn--sm share__rotate" disabled={busy} onClick={() => void run(() => rotateLink(sharingUrl))}>
                  Reset link
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  )
}
